import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useData } from '../data/DataProvider';
import EmptyState from '../shared/components/EmptyState';
import Skeleton from '../shared/components/Skeleton';
import { Icons } from '../shared/icons';
import SafeIcon from '../common/SafeIcon';

export default function FaultsDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { data, ready } = useData();

  const fault = (data.faults || []).find((f) => f.id === id);

  if (!ready) return <div className="page-container"><Skeleton rows={6} /></div>;

  if (!fault) {
    return (
      <div className="page-container">
        <EmptyState
          title="Fault not found"
          message="This fault code isn't in the database."
          action="Back to Faults"
          onAction={() => navigate('..', { relative: 'path' })}
        />
      </div>
    );
  }

  const steps = (fault.troubleshooting_steps || '')
    .split(/\n+/)
    .map((s) => s.trim())
    .filter(Boolean);

  const difficulty = { 1: 'Basic', 2: 'Easy', 3: 'Moderate', 4: 'Difficult', 5: 'Advanced' }[fault.difficulty_level] || '';

  return (
    <div className="page-container">
      <div className="page-header">
        <div>
          <h1>{fault.fault_code} — {fault.name}</h1>
          <p>{fault.equipment_type}{fault.category ? ` · ${fault.category}` : ''}</p>
        </div>
        <button className="secondary-button" onClick={() => navigate('..', { relative: 'path' })}>
          <SafeIcon icon={Icons.ArrowLeft} size={15} />
          Back to Faults
        </button>
      </div>

      <div className="faults-detail">
        {fault.safety_hazard && (
          <div className="safety-note">
            <SafeIcon icon={Icons.AlertTriangle} size={16} />
            <span>
              <strong>Safety hazard.</strong>{' '}
              {fault.safety_notes || 'Isolate the appliance before work and follow gas safe procedures.'}
            </span>
          </div>
        )}

        {fault.description && (
          <div className="detail-panel">
            <h3>Description</h3>
            <p style={{ margin: 0, fontSize: 13, lineHeight: 1.6, color: 'var(--ink)' }}>{fault.description}</p>
          </div>
        )}

        {fault.symptoms?.length > 0 && (
          <div className="detail-panel">
            <h3>Symptoms</h3>
            <div className="fault-symptoms">
              {fault.symptoms.map((s, i) => <span key={i} className="symptom-chip">{s}</span>)}
            </div>
          </div>
        )}

        {fault.common_causes && (
          <div className="detail-panel">
            <h3>Common causes</h3>
            <p style={{ margin: 0, fontSize: 13, lineHeight: 1.6, color: 'var(--ink)' }}>{fault.common_causes}</p>
          </div>
        )}

        {steps.length > 0 && (
          <div className="detail-panel">
            <h3>Troubleshooting steps</h3>
            <ol className="fault-steps">
              {steps.map((step, i) => <li key={i}>{step}</li>)}
            </ol>
          </div>
        )}

        <div className="detail-panel">
          <h3>Reference</h3>
          <table className="spec-table">
            <tbody>
              <tr><td>Priority</td><td style={{ textTransform: 'capitalize' }}>{fault.priority}</td></tr>
              <tr><td>Difficulty</td><td>{difficulty || (fault.difficulty_level ? `${fault.difficulty_level}/5` : '—')}</td></tr>
              <tr><td>Estimated time</td><td>{fault.estimated_time_minutes ? `${fault.estimated_time_minutes} minutes` : '—'}</td></tr>
              <tr><td>Required parts</td><td>{(fault.required_parts || []).map((p) => p?.part_number || p?.name || p).join(', ') || '—'}</td></tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
